import { Link, useParams } from 'react-router-dom';
import { projectPath } from '@/features/publicsite/projectPath';
import { publicCatalogApi } from '@/features/publicsite/catalog/api/publicCatalogApi';
import { CatalogCard } from '@/features/publicsite/catalog/CatalogCard';
import { projectStatusLabel } from '@/features/publicsite/catalog/projectStatus';
import { RESULT_TYPE_KEY } from '@/features/publicsite/catalog/researchResultType';
import type { ResearchOrganization } from '@/features/publicsite/types/research/ResearchOrganization';
import type { ResearchResult } from '@/features/common/types/ResearchResult';
import type { ResearchProject } from '@/features/publicsite/types/research/ResearchProject';
import { websiteHref } from '@/features/common/organizationProfile';
import { usePublicList } from '@/features/publicsite/usePublicList';
import { publicCatalog } from '@/i18n/dictionaries/publicCatalog';
import { useTranslation } from '@/i18n/useTranslation';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';

/**
 * One research institution's public profile (§6.2).
 *
 * Built from the list endpoint rather than a detail one: there is no
 * /public/research/organizations/{id}, and none is needed while the list carries every field this
 * page shows. Adding an endpoint the frontend can already answer would be work for nobody.
 *
 * Every block is conditional. The spec asks an institution to record its general data, contacts,
 * expertise and infrastructure, but it may skip any of them — and a heading over an empty line
 * claims the institution said nothing when it was never asked twice.
 */
export function ResearchOrganizationProfilePage() {
  const { t } = useTranslation(publicCatalog);
  const { id } = useParams<{ id: string }>();
  const organizations = usePublicList<ResearchOrganization>(
    publicCatalogApi.researchOrganizationsUrl,
  );
  // The work itself, filtered from the two lists the public zone already fetches. There is no
  // /public/research/organizations/{id}/projects and none is needed: every project and every
  // result carries the institution it belongs to, which is the arrangement the server chose so an
  // institution's page costs no round trip of its own.
  const projects = usePublicList<ResearchProject>(publicCatalogApi.researchProjectsUrl);
  const results = usePublicList<ResearchResult>(publicCatalogApi.researchResultsUrl);

  const back = (
    <Link className="head-link" to="/cercetare/organizatii">
      {t('orgBack')}
    </Link>
  );

  // Resolved before the early returns below rather than after them: a hook cannot be called
  // conditionally, and this page has three exits (loading, unknown id, found).
  const org = organizations?.find((o) => o.id === id) ?? null;
  usePublicPageTitle(organizations === null ? null : (org?.name ?? t('orgNotFound')));

  if (organizations === null) {
    return (
      <section className="container section">
        <p className="empty-state">{t('loading')}</p>
      </section>
    );
  }

  if (!org) {
    return (
      <section className="container section">
        <p className="eyebrow">{t('eyebrowCatalog')}</p>
        <h1>{t('orgNotFound')}</h1>
        <p>{back}</p>
      </section>
    );
  }

  const contactLine = [org.contact?.contactPerson, org.contact?.email].filter(Boolean).join(' · ');
  const ownProjects = (projects ?? []).filter((p) => p.organizationId === org.id);
  // Results carry their institution by name rather than by id, and every row in this list belongs
  // to a published institution, so the name identifies it here.
  const ownResults = (results ?? []).filter((r) => r.organization === org.name);

  return (
    <section className="container section">
      <div className="section-head">
        <div>
          <span className="eyebrow">{t('eyebrowCatalog')}</span>
          <h1>{org.name}</h1>
          <p>
            {t('orgProjects', { count: org.projectCount })} ·{' '}
            {t('orgResults', { count: org.resultCount })}
          </p>
        </div>
        {back}
      </div>

      {org.expertiseAreas.length > 0 && (
        <div className="org-profile__field">
          <h2>{t('orgExpertiseHeading')}</h2>
          <p>{org.expertiseAreas.join(' · ')}</p>
        </div>
      )}
      {org.overview && (
        <div className="org-profile__field">
          <h2>{t('orgOverviewHeading')}</h2>
          <p>{org.overview}</p>
        </div>
      )}
      {org.infrastructure && (
        <div className="org-profile__field">
          <h2>{t('orgInfrastructureHeading')}</h2>
          <p>{org.infrastructure}</p>
        </div>
      )}
      {(contactLine || org.website) && (
        <div className="org-profile__field">
          <h2>{t('orgContactHeading')}</h2>
          <p>
            {contactLine}
            {org.website && (
              <>
                {contactLine && ' · '}
                {websiteHref(org.website) ? (
                  <a href={websiteHref(org.website)!} target="_blank" rel="noreferrer">
                    {org.website}
                  </a>
                ) : (
                  org.website
                )}
              </>
            )}
          </p>
        </div>
      )}

      {/*
        What the header counted. The page said "1 proiect · 2 rezultate" and then showed neither, so
        a reader who arrived from a project card found the trail ending where it had promised to
        continue. Each grid renders only when its list holds something — an institution registered
        without published work is an ordinary state, and a heading over an empty grid reads as a
        page that failed to load.
      */}
      {ownProjects.length > 0 && (
        <>
          <div className="section-head">
            <div>
              <h2>{t('orgProjectsHeading')}</h2>
            </div>
          </div>
          <div className="card-grid">
            {ownProjects.map((p) => (
              <CatalogCard
                key={p.id}
                id={`proiect-${p.id}`}
                to={projectPath(p)}
                badge={projectStatusLabel(t, p.status)}
                title={p.title}
                subtitle={p.summary}
              >
                <p className="muted">{t('researchResults', { count: p.resultCount })}</p>
              </CatalogCard>
            ))}
          </div>
        </>
      )}

      {ownResults.length > 0 && (
        <>
          <div className="section-head">
            <div>
              <h2>{t('orgResultsHeading')}</h2>
            </div>
          </div>
          <div className="card-grid">
            {ownResults.map((r) => (
              <CatalogCard
                key={r.id}
                id={`rezultat-${r.id}`}
                badge={r.year != null ? String(r.year) : t('researchNoYear')}
                title={r.title}
                subtitle={r.journal}
              >
                <p className="muted">{t(RESULT_TYPE_KEY[r.type])}</p>
              </CatalogCard>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
